/**
 * Read-only transfer metadata for view / review modals.
 * Route and narrative fields are full width; compact facts use a 2-column grid.
 */

import React from 'react';
import TransferStatusBadge from './TransferStatusBadge';
import TransferActor from './TransferActor';
import TransferRouteStack from './TransferRouteStack';
import {
  formatTransferDateTime,
  getTransferInitiationDate,
  getTransferInitiationDateLabel,
} from '../transferDateUtils';

const TransferViewDetails = ({ transfer, variant = 'location' }) => {
  if (!transfer) return null;

  const initiationDate = getTransferInitiationDate(transfer);
  const facts = [
    {
      key: 'status',
      label: 'Status',
      value: <TransferStatusBadge status={transfer.status} size="small" />,
    },
    {
      key: 'requested-by',
      label: 'Requested by',
      value: (
        <TransferActor
          name={transfer.requested_by_name}
          role={transfer.requested_by_role}
        />
      ),
    },
  ];

  if (initiationDate) {
    facts.push({
      key: 'initiation-date',
      label: getTransferInitiationDateLabel(transfer),
      value: formatTransferDateTime(initiationDate),
    });
  }

  if (variant === 'department') {
    facts.push({
      key: 'recipient',
      label: 'Recipient',
      value: (
        <TransferActor
          name={transfer.destination_custodian_name}
          role={transfer.destination_custodian_role}
        />
      ),
    });
  }

  if (transfer.previous_physical_location) {
    facts.push({
      key: 'source-location',
      label: 'Source physical location',
      value: transfer.previous_physical_location,
    });
  }

  if (variant === 'location' && transfer.destination_physical_location) {
    facts.push({
      key: 'destination-location',
      label: 'Destination physical location',
      value: transfer.destination_physical_location,
    });
  }

  return (
    <div className="transfer-view-details">
      <div className="transfer-view-details__section transfer-view-details__section--route">
        <span className="transfer-view-details__label">Route</span>
        <div className="transfer-view-details__value">
          {variant === 'location' ? (
            <TransferRouteStack
              fromLocation={transfer.from_location_name}
              toLocation={transfer.to_location_name}
              fromDepartment={transfer.previous_department_name}
              toDepartment={transfer.destination_department_name}
            />
          ) : (
            <span className="transfer-route-text">
              {transfer.from_department_name}
              <span className="transfer-route-arrow" aria-hidden="true">
                →
              </span>
              {transfer.to_department_name}
            </span>
          )}
        </div>
      </div>

      <div className="transfer-view-details__facts">
        {facts.map((fact) => (
          <div key={fact.key} className="transfer-view-details__fact">
            <span className="transfer-view-details__fact-label">{fact.label}</span>
            <span className="transfer-view-details__fact-value">{fact.value}</span>
          </div>
        ))}
      </div>

      {transfer.request_reason && (
        <div className="transfer-view-details__section transfer-view-details__section--narrative">
          <span className="transfer-view-details__label">Request reason</span>
          <p className="transfer-view-details__narrative">{transfer.request_reason}</p>
        </div>
      )}
    </div>
  );
};

export default TransferViewDetails;
